import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IoPause, IoPlay, IoPlaySkipForward, IoRefresh } from 'react-icons/io5';
import { reportApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import BibleProgressCalendar from '../components/progress/BibleProgressCalendar';
import {
  buildReplay,
  dayDurationMs,
  dayIndexAt,
  eventIndexAt,
  formatReplayDate,
  moveCounts,
  summarizeFrame
} from '../utils/seasonReplay';
import { formatSeasonStart } from '../utils/season';
import './SeasonReplayPage.css';

const SPEEDS = [1, 2, 4];
// How long a cell stays lit after its read plays, in real time.
const FLASH_MS = 500;
// A beat on the empty map before playback starts, so the start registers.
const START_DELAY_MS = 700;

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
);

const formatNumber = (value) => value.toLocaleString();

/**
 * The season replay: the community Bible map from an empty grid on the
 * season's first day to today, with each chapter lighting up in the order it
 * was read. Days play for equal time, so the date counter keeps a steady beat.
 */
const SeasonReplayPage = () => {
  const [chapters, setChapters] = useState(null);
  const [replayData, setReplayData] = useState(null);
  const [error, setError] = useState(null);

  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [frame, setFrame] = useState({ counts: {}, index: 0, t: 0, flash: null });

  // Playback state lives in refs so the animation loop does not re-subscribe.
  const countsRef = useRef({});
  const indexRef = useRef(0);
  const playheadRef = useRef(0);
  const litAtRef = useRef(new Map());
  const speedRef = useRef(speed);
  speedRef.current = speed;

  const load = useCallback(async () => {
    setError(null);
    try {
      const [statsResponse, replayResponse] = await Promise.all([
        reportApi.getGlobalStats(),
        reportApi.getSeasonReplay()
      ]);
      setChapters(statsResponse.data.stats.chapters);
      setReplayData(replayResponse.data);
    } catch (err) {
      console.error('Failed to load season replay:', err);
      setError(err.response?.status === 429
        ? 'Too many requests. Please wait a moment and try again.'
        : 'Failed to load the season replay. Please try again.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const replay = useMemo(() => (replayData ? buildReplay(replayData) : null), [replayData]);
  const dayCount = replay?.dates.length || 0;
  const dayMs = dayDurationMs(dayCount);

  // Move the playhead to `t` (in days) and render that frame.
  const seek = useCallback((t, { flash = false } = {}) => {
    if (!replay) return;
    const clamped = Math.min(dayCount, Math.max(0, t));
    const to = eventIndexAt(replay, clamped);
    const from = indexRef.current;
    moveCounts(countsRef.current, replay.events, from, to);

    const now = performance.now();
    const litAt = litAtRef.current;
    if (flash && to > from) {
      for (let i = from; i < to; i += 1) litAt.set(replay.events[i], now);
    } else if (!flash) {
      litAt.clear();
    }
    for (const [id, at] of litAt) {
      if (now - at > FLASH_MS) litAt.delete(id);
    }

    indexRef.current = to;
    playheadRef.current = clamped;
    setFrame({
      counts: { ...countsRef.current },
      index: to,
      t: clamped,
      flash: new Set(litAt.keys())
    });
  }, [replay, dayCount]);

  // Opening frame: an empty map that starts playing, or (reduced motion) the
  // finished map, paused, with the scrubber still there to explore.
  useEffect(() => {
    if (!replay) return undefined;
    countsRef.current = {};
    indexRef.current = 0;
    litAtRef.current = new Map();
    if (prefersReducedMotion()) {
      seek(dayCount);
      return undefined;
    }
    seek(0);
    const timer = setTimeout(() => setPlaying(true), START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [replay, dayCount, seek]);

  useEffect(() => {
    if (!playing || !replay) return undefined;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const elapsed = now - last;
      last = now;
      const next = playheadRef.current + (elapsed * speedRef.current) / dayMs;
      if (next >= dayCount) {
        seek(dayCount, { flash: true });
        setPlaying(false);
        return;
      }
      seek(next, { flash: true });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, replay, dayCount, dayMs, seek]);

  // Let the last cells finish flashing after playback stops.
  useEffect(() => {
    if (playing || !frame.flash || frame.flash.size === 0) return undefined;
    const timer = setTimeout(() => seek(playheadRef.current), FLASH_MS);
    return () => clearTimeout(timer);
  }, [playing, frame.flash, seek]);

  const atEnd = frame.t >= dayCount;

  const togglePlay = () => {
    if (playing) {
      setPlaying(false);
      return;
    }
    if (atEnd) {
      countsRef.current = {};
      indexRef.current = 0;
      seek(0);
    }
    setPlaying(true);
  };

  const onScrub = (event) => {
    setPlaying(false);
    seek(Number(event.target.value));
  };

  const jumpToToday = () => {
    setPlaying(false);
    seek(dayCount);
  };

  if (error) {
    return (
      <div className="season-replay-page">
        <div className="season-replay-error">
          <h2>Unable to load the replay</h2>
          <p>{error}</p>
          <button type="button" onClick={load} className="retry-button">Try again</button>
        </div>
      </div>
    );
  }

  if (!replay || !chapters) {
    return (
      <div className="season-replay-page loading">
        <LoadingSpinner size="large" message="Loading the season..." />
      </div>
    );
  }

  const dayIndex = dayIndexAt(replay, frame.t);
  const currentDate = replay.dates[dayIndex];
  const summary = summarizeFrame(frame.counts, chapters.length, frame.index);
  const title = replayData.challenge?.name || 'This season';

  return (
    <div className="season-replay-page">
      <header className="season-replay-header">
        <p className="season-replay-eyebrow">
          Season replay · since {formatSeasonStart(replay.dates[0])}
        </p>
        <h1>{title}</h1>
        <p className="season-replay-intro">
          Every chapter our community has read this season, lighting up in the order it was read.
        </p>
      </header>

      <div className="season-replay-stage">
        {/* Stays pinned while the map scrolls beneath it on short screens. */}
        <div className="season-replay-bar">
          <div className="season-replay-hud">
            <div className="season-replay-date">
              <span className="season-replay-date-label">
                Day {dayIndex + 1} of {dayCount}
              </span>
              <time dateTime={currentDate}>{formatReplayDate(currentDate)}</time>
            </div>
            <dl className="season-replay-stats">
              <div>
                <dt>Chapters read</dt>
                <dd>{formatNumber(summary.chaptersRead)}</dd>
              </div>
              <div>
                <dt>Still unread</dt>
                <dd>{formatNumber(summary.unreadChapters)}</dd>
              </div>
              <div>
                <dt>Complete Bibles</dt>
                <dd>{formatNumber(summary.completions)}</dd>
              </div>
            </dl>
          </div>

          <div className="season-replay-controls">
            <button
              type="button"
              className="season-replay-play"
              onClick={togglePlay}
              aria-label={playing ? 'Pause' : atEnd ? 'Replay from the start' : 'Play'}
            >
              {playing ? <IoPause aria-hidden="true" /> : atEnd ? <IoRefresh aria-hidden="true" /> : <IoPlay aria-hidden="true" />}
            </button>

            <input
              type="range"
              className="season-replay-scrubber"
              min={0}
              max={dayCount}
              step="any"
              value={frame.t}
              onChange={onScrub}
              aria-label="Season timeline"
              aria-valuetext={formatReplayDate(currentDate)}
            />

            <div className="season-replay-speeds" role="group" aria-label="Playback speed">
              {SPEEDS.map(option => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={speed === option}
                  onClick={() => setSpeed(option)}
                >
                  {option}×
                </button>
              ))}
            </div>

            <button
              type="button"
              className="season-replay-skip"
              onClick={jumpToToday}
              disabled={atEnd}
              aria-label="Jump to today"
              title="Jump to today"
            >
              <IoPlaySkipForward aria-hidden="true" />
            </button>
          </div>
        </div>

        <BibleProgressCalendar
          chapters={chapters}
          chapterReadCounts={frame.counts}
          flashChapterIds={frame.flash}
          replay
        />
      </div>

      {atEnd && !playing && (
        <div className="season-replay-end">
          <p>That&rsquo;s where we are today.</p>
          <Link to="/" className="season-replay-cta">Keep reading →</Link>
        </div>
      )}
    </div>
  );
};

export default SeasonReplayPage;
